import { SUPER_ADMIN_WILDCARD } from './types'

import type { NavigationItem } from '@/types/layout'

/**
 * Remove navigation items the current admin can't access.
 * Rules:
 *   - SuperAdmin (wildcard `*`) sees everything.
 *   - An item is hidden when it declares `requiredPermissions` and the admin
 *     doesn't hold any of them (OR-list — any single match grants visibility).
 *   - A parent with children is dropped when every child was filtered out.
 */
export const filterNavigationByPermissions = (
  items: NavigationItem[],
  permissions: string[],
  isSuperAdmin: boolean,
): NavigationItem[] => {
  if (isSuperAdmin || permissions.includes(SUPER_ADMIN_WILDCARD)) {
    return items
  }
  const held = new Set(permissions)

  const visit = (list: NavigationItem[]): NavigationItem[] => {
    const out: NavigationItem[] = []
    for (const item of list) {
      const required = item.requiredPermissions ?? []
      const passesSelf =
        required.length === 0 || required.some((code) => held.has(code))

      if (item.children && item.children.length > 0) {
        const keptChildren = visit(item.children)
        if (keptChildren.length === 0) continue
        if (!passesSelf) continue
        out.push({ ...item, children: keptChildren })
      } else {
        if (!passesSelf) continue
        out.push(item)
      }
    }
    return out
  }

  return visit(items)
}
